import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useFormulaActions } from 'components/formulas/useFormulaActions';
import { useMoveToDraft } from 'components/formulas/useMoveToDraft';
import DropdownMenu from 'components/nav/DrowpdownMenu/DropdownMenu';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import routes from '../../../constants/routes';

import useGetLocationState from 'hooks/helper/useGetLocationState';

import {
    ApprovedFormulaState,
    FormulaDetailModel,
    FormulaStatus,
} from 'models/formula.model';

export const DrawerActionMenu: React.FC<{
    data: FormulaDetailModel;
    onClose: () => void;
    selectFormulaToClone: (id: number) => void;
    handleTestMessageOpen: () => void;
    openEditModalHandler: () => void;
    hasDeleteAccess: boolean;
    hasCreateFormulaAccess: boolean;
    showPreviewCombinations?: boolean;
    onTogglePreviewCombinations?: () => void;
}> = ({
    data,
    onClose,
    selectFormulaToClone,
    handleTestMessageOpen,
    openEditModalHandler,
    hasDeleteAccess,
    hasCreateFormulaAccess,
    showPreviewCombinations,
    onTogglePreviewCombinations,
}) => {
    const { getLocationState } = useGetLocationState('All Messages');
    const { handleArchive, handleDelete } = useFormulaActions({
        formula: data,
        onCloseDrawer: onClose,
    });

    const hasWriteAccess = useMemo<boolean>(() => {
        return data.has_write_access ?? false;
    }, [data]);

    const isScheduledOrLiveFormula = useMemo<boolean>(
        () =>
            [
                ApprovedFormulaState.Scheduled,
                ApprovedFormulaState.Live,
            ].includes(data?.active_state as ApprovedFormulaState),
        [data],
    );

    const { moveToDraftHandler: handleMoveToDraft } = useMoveToDraft({
        formulaId: data.id,
        isControlledFormula: data.is_controlled_message,
        isScheduledOrLiveFormula: isScheduledOrLiveFormula,
        onClose: onClose,
    });

    function getArchiveOrDeleteOption(
        status: FormulaStatus,
        handleArchive: () => void,
        handleDelete: () => void,
    ) {
        if (status === FormulaStatus.Approved) {
            return {
                name: 'Move to Archive',
                action: handleArchive,
                isDisabled: false,
            };
        }
        if (hasDeleteAccess) {
            return {
                name: 'Delete',
                action: handleDelete,
                isDisabled: false,
            };
        }

        return null;
    }

    const archiveOrDeleteOption = getArchiveOrDeleteOption(
        data.status,
        handleArchive,
        handleDelete,
    );

    const viewDetailsTo = useMemo(
        () => ({
            pathname: routes.formulaView(data.active_state, data.id),
            state: getLocationState(),
        }),
        [data.active_state, data.id, getLocationState],
    );

    return (
        <Actions>
            <ViewDetailsLink to={viewDetailsTo}>View details</ViewDetailsLink>
            {onTogglePreviewCombinations && (
                <PreviewButton onClick={onTogglePreviewCombinations}>
                    {showPreviewCombinations
                        ? 'Hide combinations'
                        : 'Preview combinations'}
                </PreviewButton>
            )}
            <DropdownMenu
                wrapperStyle={{
                    height: '100%',
                    width: '32px',
                    padding: aampeTheme.spacing4(2),
                    backgroundColor: aampeTheme.colors.backgroundWG02,
                    borderRadius: aampeTheme.borderRadius.default,
                }}
                isVertical={false}
                options={[
                    ...(String(data.status) !== ApprovedFormulaState.Expired &&
                    data.status !== FormulaStatus.Draft &&
                    data.status !== FormulaStatus.Archived &&
                    hasWriteAccess
                        ? [
                              {
                                  name: 'Edit',
                                  action: () => openEditModalHandler(),
                                  isDisabled: false,
                              },
                          ]
                        : []),
                    {
                        name: 'Send a test message',
                        action: () => handleTestMessageOpen(),
                        isDisabled: false,
                    },
                    ...(hasCreateFormulaAccess
                        ? [
                              {
                                  name: 'Clone',
                                  action: () => selectFormulaToClone(data.id),
                                  isDisabled: false,
                              },
                          ]
                        : []),
                    ...(data.status === FormulaStatus.Approved && hasWriteAccess
                        ? [
                              {
                                  name: 'Move to draft',
                                  action: handleMoveToDraft,
                                  isDisabled: false,
                              },
                          ]
                        : []),
                    ...(archiveOrDeleteOption ? [archiveOrDeleteOption] : []),
                ]}
            ></DropdownMenu>
        </Actions>
    );
};
const Actions = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(0, 5, 7, 5),
});
const ViewDetailsLink = styled(Link)({
    cursor: 'pointer',
    backgroundColor: aampeTheme.colors.solidA01,
    color: aampeTheme.colors.white,
    border: 'none',
    borderRadius: aampeTheme.borderRadius.default,
    fontSize: aampeTheme.size.font.textSmall,
    height: aampeTheme.spacing4(8),
    padding: aampeTheme.spacing4(0, 4),
    display: 'flex',
    flex: 2,
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: aampeTheme.weights.heavy,
    textDecoration: 'none',
    '&:hover': {
        backgroundColor: aampeTheme.colors.solidA02,
    },
});

const PreviewButton = styled.button({
    all: 'unset',
    cursor: 'pointer',
    flex: 2,
    backgroundColor: aampeTheme.colors.backgroundWG02,
    color: aampeTheme.colors.solidA01,
    border: `1px solid ${aampeTheme.colors.solidA01}`,
    borderRadius: aampeTheme.borderRadius.default,
    fontSize: aampeTheme.size.font.textSmall,
    height: aampeTheme.spacing4(8),
    padding: aampeTheme.spacing4(0, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: aampeTheme.weights.heavy,
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG01,
    },
});
